/**
 * EmailSearch — search + filter bar.
 * Controls store signals: searchText, quickFilter, sortOrder, advancedFilters.
 * Every signal change is debounced and triggers fetchEmails(1).
 */

import { h } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import { Popover } from 'bootstrap'
import { buildSearchHelpHtml, SEARCH_DEBOUNCE_MS } from '@tshmanager/email-shared'

const SEARCH_HELP_HTML = buildSearchHelpHtml()

export function EmailSearch({ store, i18n, isMobile }) {
  const searchText    = store.searchText.value
  const quickFilter   = store.quickFilter.value
  const sortOrder     = store.sortOrder.value
  const filtersOpen   = store.filtersOpen.value
  const advFilters    = store.advancedFilters.value
  const debounceTimer = useRef(null)

  function debounce(fn) {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(fn, SEARCH_DEBOUNCE_MS)
  }

  useEffect(() => () => clearTimeout(debounceTimer.current), [])

  function handleSearchInput(e) {
    store.setSearchText(e.target.value)
    debounce(() => store.fetchEmails(1))
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter') {
      clearTimeout(debounceTimer.current)
      store.fetchEmails(1)
    }
    if (e.key === 'Escape') {
      store.setSearchText('')
      store.fetchEmails(1)
    }
  }

  function handleQuickFilter(chip) {
    store.toggleQuickFilter(chip)
    store.fetchEmails(1)
  }

  function handleSort(e) {
    store.setSort(e.target.value)
    store.fetchEmails(1)
  }

  function handleAdvancedFilter(key, value) {
    store.setAdvancedFilter(key, value)
    debounce(() => store.fetchEmails(1))
  }

  function handleClearAll() {
    store.clearAllFilters()
    store.fetchEmails(1)
  }

  const hasActiveFilters = searchText || quickFilter || sortOrder !== 'newest' ||
    Object.values(advFilters).some(v => v)

  return (
    <div class="email-search-bar border-bottom">

      {/* Search input row */}
      <div class="d-flex align-items-center gap-1 px-2 py-1">
        {isMobile && (
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0 flex-shrink-0 me-1"
            onClick={() => store.showSidebarPanel()}
            title={i18n?.t('emails.folders_nav') || 'Mappák'}
          >
            <i class="bi bi-list" style="font-size:1.1rem" />
          </button>
        )}
        <div class="input-group input-group-sm flex-grow-1">
          <span class="input-group-text border-0 bg-transparent ps-1 pe-0">
            <i class="bi bi-search text-muted" style="font-size:0.8rem" />
          </span>
          <input
            type="search"
            class="form-control form-control-sm border-0 shadow-none bg-transparent"
            placeholder={i18n?.t('emails.search_placeholder')}
            value={searchText}
            onInput={handleSearchInput}
            onKeyDown={handleSearchKeyDown}
          />
          {searchText && (
            <button
              class="btn btn-link btn-sm text-muted p-0 pe-1"
              onClick={() => { store.setSearchText(''); store.fetchEmails(1) }}
            >
              <i class="bi bi-x" />
            </button>
          )}
        </div>

        {/* Search syntax help popover */}
        <button
          type="button"
          class="btn btn-sm btn-link text-muted p-0 flex-shrink-0"
          tabIndex="0"
          data-bs-toggle="popover"
          data-bs-trigger="focus hover"
          data-bs-html="true"
          data-bs-title={i18n?.t('emails.search_help_title')}
          data-bs-content={SEARCH_HELP_HTML}
          data-bs-placement="bottom"
          style="line-height:1"
          ref={el => {
            if (el && !el._bsPopover) {
              el._bsPopover = new Popover(el)
            }
          }}
        >
          <i class="bi bi-question-circle" style="font-size:0.85rem" />
        </button>

        {/* Sort dropdown */}
        <select
          class="form-select form-select-sm border-0 shadow-none bg-transparent w-auto"
          value={sortOrder}
          onChange={handleSort}
          style="font-size:0.8rem;cursor:pointer"
        >
          <option value="newest">{i18n?.t('emails.sort.newest')}</option>
          <option value="oldest">{i18n?.t('emails.sort.oldest')}</option>
          <option value="sender">{i18n?.t('emails.sort.sender')}</option>
          <option value="subject">{i18n?.t('emails.sort.subject')}</option>
        </select>

        {/* Advanced filter toggle */}
        <button
          class={`btn btn-sm btn-link p-0 ${filtersOpen || hasActiveFilters ? 'text-primary' : 'text-muted'}`}
          onClick={store.toggleFiltersPanel}
          title={i18n?.t('emails.advanced_filters')}
        >
          <i class="bi bi-sliders" />
          {hasActiveFilters && <span class="ms-1 badge bg-primary rounded-pill" style="font-size:0.6rem;vertical-align:middle">!</span>}
        </button>
      </div>

      {/* Quick filter chips */}
      <div class="d-flex gap-1 px-2 pb-1 flex-wrap">
        {[
          { key: 'unread',     label: i18n?.t('emails.filter.unread'),      icon: 'bi-envelope' },
          { key: 'starred',    label: i18n?.t('emails.filter.starred'),     icon: 'bi-star' },
          { key: 'attachment', label: i18n?.t('emails.filter.attachment'),  icon: 'bi-paperclip' },
        ].map(chip => (
          <button
            key={chip.key}
            class={`btn btn-xs d-flex align-items-center gap-1 rounded-pill px-2 py-0 ${quickFilter === chip.key ? 'btn-primary' : 'btn-outline-secondary'}`}
            style="font-size:0.72rem;height:22px"
            onClick={() => handleQuickFilter(chip.key)}
          >
            <i class={`bi ${chip.icon}`} style="font-size:0.65rem" />
            {chip.label}
          </button>
        ))}
        {hasActiveFilters && (
          <button
            class="btn btn-xs btn-link text-muted p-0 px-1 rounded-pill"
            style="font-size:0.72rem;height:22px"
            onClick={handleClearAll}
          >
            <i class="bi bi-x-circle me-1" style="font-size:0.65rem" />
            {i18n?.t('emails.clear_filters')}
          </button>
        )}
      </div>

      {/* Advanced filters panel */}
      {filtersOpen && (
        <div class="email-advanced-filters px-2 pb-2 border-top pt-2">
          <div class="row g-2 align-items-end">
            <div class="col-auto">
              <label class="form-label mb-0 small text-muted">{i18n?.t('emails.filter.status')}</label>
              <select
                class="form-select form-select-sm"
                value={advFilters.status || ''}
                onChange={e => handleAdvancedFilter('status', e.target.value)}
              >
                <option value="">{i18n?.t('helpers.all')}</option>
                <option value="open">{i18n?.t('emails.status.open')}</option>
                <option value="inprogress">{i18n?.t('emails.status.inprogress')}</option>
                <option value="closed">{i18n?.t('emails.status.closed')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
